import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { PlayArrow as TestIcon } from '@mui/icons-material';
import { CatalogConnector, CatalogConfigField, CONNECTOR_COLORS } from '../../types/connectorCatalog';
import { apiService, ConnectorConfig } from '../../services/api';

interface StreamInfo {
  name: string;
  type: string; // "file" | "table"
}

/** Empty → first input: add scheme unless user is typing it char-by-char or already used a scheme. */
function needsSchemeAutoPrefixFromEmpty(previous: string, next: string, scheme: string): boolean {
  if (previous.trim() !== '') return false;
  const t = next.trimStart();
  if (!t) return false;
  if (t.includes('://')) return false;
  const tl = t.toLowerCase();
  const schemeLower = scheme.toLowerCase();
  if (schemeLower.startsWith(tl) && tl.length < schemeLower.length) return false;
  return true;
}

interface ConnectorSetupDialogProps {
  open: boolean;
  connector: CatalogConnector | null;
  /** When editing an existing saved connector, pass its full config here */
  existingConnector?: {
    connector_id: string;
    name: string;
    direction: 'source' | 'destination' | 'bidirectional';
    config: Record<string, any>;
  } | null;
  onClose: () => void;
  onSave: (data: {
    connector_type: string;
    name: string;
    direction: 'source' | 'destination';
    config: Record<string, any>;
    catalog_connector_id: string;
    project_id?: string;
  }) => void;
  /** Called instead of onSave when updating an existing connector */
  onUpdate?: (connectorId: string, data: {
    name: string;
    config: Record<string, any>;
    direction?: string;
    connector_type?: string;
  }) => void;
  projectId?: string;
}

const ConnectorSetupDialog: React.FC<ConnectorSetupDialogProps> = ({
  open,
  connector,
  existingConnector,
  onClose,
  onSave,
  onUpdate,
  projectId,
}) => {
  const theme = useTheme();
  const [connectorName, setConnectorName] = useState('');
  const [connectorDirection, setConnectorDirection] = useState<'source' | 'destination'>('source');
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [associateWithProject, setAssociateWithProject] = useState(!!projectId);

  // Stream discovery state
  const [availableStreams, setAvailableStreams] = useState<StreamInfo[]>([]);
  const [selectedStreams, setSelectedStreams] = useState<string[]>([]);
  const [discoveringStreams, setDiscoveringStreams] = useState(false);
  const [nameError, setNameError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const expectedBucketScheme = useMemo(() => {
    if (!connector) return null;
    if (connector.connector_id === 's3') return 's3://';
    if (connector.connector_id === 'gcs') return 'gs://';
    return null;
  }, [connector]);

  const normalizeBucketUrl = useCallback(
    (value: string): string => {
      if (!expectedBucketScheme) return value;
      let trimmed = value.trim();
      if (!trimmed) return '';

      if (expectedBucketScheme === 'gs://' && trimmed.toLowerCase().startsWith('gcs://')) {
        trimmed = `gs://${trimmed.slice(6)}`;
      }

      const schemeLower = expectedBucketScheme.toLowerCase();
      const schemeLen = expectedBucketScheme.length;
      // Collapse duplicate leading scheme(s), e.g. gs://gs://bucket → bucket → gs://bucket
      while (trimmed.toLowerCase().startsWith(schemeLower)) {
        trimmed = trimmed.slice(schemeLen).trimStart();
      }
      if (!trimmed) return '';

      if (trimmed.includes('://')) {
        return trimmed;
      }
      return `${expectedBucketScheme}${trimmed}`;
    },
    [expectedBucketScheme]
  );

  const getBucketUrlError = useCallback(
    (value: string): string => {
      if (!expectedBucketScheme) return '';
      const normalizedValue = normalizeBucketUrl(value);
      if (!normalizedValue) return '';
      const lower = normalizedValue.toLowerCase();
      if (expectedBucketScheme === 's3://' && !lower.startsWith('s3://')) {
        return 'S3 bucket URL must start with s3://';
      }
      if (expectedBucketScheme === 'gs://' && !lower.startsWith('gs://')) {
        return 'GCS bucket URL must start with gs://';
      }
      return '';
    },
    [expectedBucketScheme, normalizeBucketUrl]
  );

  const buildNormalizedConfig = useCallback((): Record<string, any> => {
    const config: Record<string, any> = { ...formValues };
    if (typeof config.bucket_url === 'string') {
      config.bucket_url = normalizeBucketUrl(config.bucket_url);
    }
    return config;
  }, [formValues, normalizeBucketUrl]);

  // Fetch existing connections for the project to check for duplicate names
  const connectionsQuery = useQuery<ConnectorConfig[]>(
    ['connectors', projectId],
    () => apiService.listConnectors({ project_id: projectId }),
    { enabled: !!projectId && open, staleTime: 30000 }
  );

  const isEditing = !!existingConnector;

  // Reset state when dialog opens with new connector or existing connector data
  React.useEffect(() => {
    if (connector && open) {
      if (existingConnector) {
        // Editing an existing saved connector — populate from saved values
        setConnectorName(existingConnector.name);
        const dir = existingConnector.direction === 'bidirectional' ? 'source' : existingConnector.direction;
        setConnectorDirection(dir as 'source' | 'destination');
        // Merge defaults with saved config (saved config wins)
        const merged: Record<string, any> = {};
        connector.config_fields.forEach((f) => {
          if (f.default !== undefined && f.default !== null) {
            merged[f.name] = f.default;
          }
        });
        Object.entries(existingConnector.config).forEach(([key, value]) => {
          // Skip internal keys like _selected_streams
          if (!key.startsWith('_')) {
            merged[key] = value;
          }
        });
        // Also preserve _selected_streams if present
        if (existingConnector.config._selected_streams) {
          setSelectedStreams(existingConnector.config._selected_streams);
        }
        setFormValues(merged);
      } else {
        // Creating a new connector — leave name empty so user enters their own
        setConnectorName('');
        setConnectorDirection(connector.direction === 'destination' ? 'destination' : 'source');
        const defaults: Record<string, any> = {};
        connector.config_fields.forEach((f) => {
          if (f.default !== undefined && f.default !== null) {
            defaults[f.name] = f.default;
          }
        });
        setFormValues(defaults);
        // Reset project association to true if projectId is available
        setAssociateWithProject(!!projectId);
      }
      setTestResult(null);
      setNameError('');
      setFieldErrors({});
      setAvailableStreams([]);
      if (!existingConnector?.config._selected_streams) {
        setSelectedStreams([]);
      }
    }
  }, [connector, existingConnector, open, projectId]);

  const brandColor = connector ? (CONNECTOR_COLORS[connector.connector_id] || theme.palette.primary.main) : theme.palette.primary.main;
  const isInstalled = connector ? connector.installed !== false : true;

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: value }));
    if (fieldName === 'bucket_url' && typeof value === 'string') {
      const error = getBucketUrlError(value);
      setFieldErrors((prev) => ({ ...prev, [fieldName]: error }));
    } else {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: '' }));
    }
  }, [getBucketUrlError]);

  const validateName = useCallback((newName: string): boolean => {
    if (!newName.trim()) {
      setNameError('Connection name is required');
      return false;
    }

    // Check if name already exists (case-insensitive)
    const existingNames = (connectionsQuery.data || [])
      .filter((conn: ConnectorConfig) => 
        // Exclude current connector if we're editing
        !existingConnector || conn.connector_id !== existingConnector.connector_id
      )
      .map((conn: ConnectorConfig) => conn.name.toLowerCase());

    if (existingNames.includes(newName.toLowerCase().trim())) {
      setNameError(`A connection with the name "${newName}" already exists. Please use a unique name.`);
      return false;
    }

    setNameError('');
    return true;
  }, [connectionsQuery.data, existingConnector]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setConnectorName(newName);
    if (!newName) {
      setNameError('');
    } else {
      validateName(newName);
    }
  };

  const shouldShowField = useCallback(
    (field: CatalogConfigField): boolean => {
      if (!field.depends_on) return true;
      for (const [depFieldName, allowedValues] of Object.entries(field.depends_on)) {
        const currentValue = formValues[depFieldName];
        // Compare as strings to handle booleans (true matches "true"/"True")
        const currentStr = String(currentValue);
        if (!allowedValues.includes(currentValue) && !allowedValues.includes(currentStr)) return false;
      }
      return true;
    },
    [formValues]
  );

  const handleTestConnection = useCallback(async () => {
    if (!connector) return;
    setTestLoading(true);
    setTestResult(null);
    setAvailableStreams([]);
    setSelectedStreams([]);

    const connectorType =
      connectorDirection === 'destination'
        ? `dlt_${connector.connector_id}_destination`
        : `dlt_${connector.connector_id}_source`;

    try {
      const normalizedConfig = buildNormalizedConfig();
      const result = await apiService.testConnector({
        connector_type: connectorType,
        config: normalizedConfig,
      });
      setTestResult({ success: result.success, message: result.message ?? '' });

      // If test succeeded, discover available streams/tables
      if (result.success) {
        setDiscoveringStreams(true);
        try {
          const discoverData = await apiService.discoverStreams({
            connectorType,
            config: normalizedConfig,
          });
          if (discoverData.streams?.length) {
            setAvailableStreams(discoverData.streams);
            setSelectedStreams(discoverData.streams.map((s: StreamInfo) => s.name));
          }
        } catch {
          // Stream discovery is optional — don't block save
        } finally {
          setDiscoveringStreams(false);
        }
      }
    } catch (error) {
      setTestResult({ success: false, message: String(error) });
    } finally {
      setTestLoading(false);
    }
  }, [connector, connectorDirection, buildNormalizedConfig]);

  const handleStreamToggle = useCallback((streamName: string) => {
    setSelectedStreams((prev) =>
      prev.includes(streamName) ? prev.filter((s) => s !== streamName) : [...prev, streamName]
    );
  }, []);

  const handleSelectAllStreams = useCallback(() => {
    if (selectedStreams.length === availableStreams.length) {
      setSelectedStreams([]);
    } else {
      setSelectedStreams(availableStreams.map((s) => s.name));
    }
  }, [availableStreams, selectedStreams.length]);

  const handleSave = useCallback(() => {
    if (!connector) return;

    const config = buildNormalizedConfig();
    // Store selected streams in config if any were discovered
    if (availableStreams.length > 0 && selectedStreams.length > 0) {
      config._selected_streams = selectedStreams;
    }
    // Preserve existing _selected_streams if editing and not rediscovered
    if (isEditing && existingConnector?.config._selected_streams && !config._selected_streams) {
      config._selected_streams = existingConnector.config._selected_streams;
    }
    if (isEditing && existingConnector && onUpdate) {
      // Update existing connector (include direction so type changes persist)
      const connectorType =
        connectorDirection === 'destination'
          ? `dlt_${connector.connector_id}_destination`
          : `dlt_${connector.connector_id}_source`;
      onUpdate(existingConnector.connector_id, {
        name: connectorName,
        config,
        direction: connectorDirection,
        connector_type: connectorType,
      });
    } else {
      // Create new connector
      const connectorType =
        connectorDirection === 'destination'
          ? `dlt_${connector.connector_id}_destination`
          : `dlt_${connector.connector_id}_source`;

      onSave({
        connector_type: connectorType,
        name: connectorName.trim(),
        direction: connectorDirection,
        config,
        catalog_connector_id: connector.connector_id,
        project_id: associateWithProject && projectId ? projectId : undefined,
      });
    }
  }, [connector, connectorName, connectorDirection, availableStreams, selectedStreams, onSave, onUpdate, isEditing, existingConnector, associateWithProject, projectId, buildNormalizedConfig]);

  const canSave = useMemo(() => {
    if (!connector || !connectorName.trim() || !!nameError) return false;
    if (typeof formValues.bucket_url === 'string' && !!getBucketUrlError(formValues.bucket_url)) return false;
    if (Object.values(fieldErrors).some(Boolean)) return false;
    // Check required fields
    for (const field of connector.config_fields) {
      if (field.required && shouldShowField(field) && !formValues[field.name]) {
        return false;
      }
    }
    return true;
  }, [connector, connectorName, formValues, shouldShowField, nameError, fieldErrors, getBucketUrlError]);

  const renderConfigField = (field: CatalogConfigField) => {
    if (!shouldShowField(field)) return null;

    const value = formValues[field.name] ?? field.default ?? '';

    if (field.type === 'boolean') {
      const checked = value === true || value === 'true' || value === 'True';
      return (
        <Box key={field.name}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={checked}
                onChange={(e) => handleFieldChange(field.name, e.target.checked)}
              />
            }
            label={field.label}
          />
          {field.description && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5, mt: -0.5, display: 'block' }}>
              {field.description}
            </Typography>
          )}
        </Box>
      );
    }

    if (field.type === 'select' && field.options?.length) {
      return (
        <FormControl fullWidth size="small" key={field.name}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value}
            label={field.label}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
          >
            {field.options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          {field.description && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
              {field.description}
            </Typography>
          )}
        </FormControl>
      );
    }

    if (field.type === 'textarea') {
      return (
        <TextField
          key={field.name}
          fullWidth
          size="small"
          label={field.label}
          value={value}
          onChange={(e) => handleFieldChange(field.name, e.target.value)}
          required={field.required}
          helperText={field.description}
          placeholder={field.placeholder}
          multiline
          rows={4}
        />
      );
    }

    const bucketValidationError =
      field.name === 'bucket_url' && typeof value === 'string'
        ? getBucketUrlError(value)
        : '';
    const bucketFieldHint =
      field.name === 'bucket_url' && expectedBucketScheme
        ? `You can enter bucket/path only. We'll auto-add ${expectedBucketScheme}`
        : field.description;
    const bucketPlaceholder =
      field.name === 'bucket_url' && expectedBucketScheme
        ? `${expectedBucketScheme}my-bucket/path`
        : field.placeholder;

    const isSchemeAutoPrefixBucketUrl =
      field.name === 'bucket_url' &&
      !!expectedBucketScheme &&
      (connector?.connector_id === 'gcs' || connector?.connector_id === 's3');

    return (
      <TextField
        key={field.name}
        fullWidth
        size="small"
        label={field.label}
        value={value}
        onChange={(e) => {
          let v = e.target.value;
          if (isSchemeAutoPrefixBucketUrl && expectedBucketScheme) {
            const prev = typeof value === 'string' ? value : '';
            if (needsSchemeAutoPrefixFromEmpty(prev, v, expectedBucketScheme)) {
              v = `${expectedBucketScheme}${v.trimStart()}`;
            }
          }
          handleFieldChange(field.name, v);
        }}
        onBlur={
          isSchemeAutoPrefixBucketUrl
            ? (e) => {
                const normalized = normalizeBucketUrl(e.target.value);
                if (normalized !== e.target.value) {
                  handleFieldChange(field.name, normalized);
                }
              }
            : undefined
        }
        required={field.required}
        type={field.type === 'password' ? 'password' : field.type === 'integer' ? 'number' : 'text'}
        error={!!(fieldErrors[field.name] || bucketValidationError)}
        helperText={fieldErrors[field.name] || bucketValidationError || bucketFieldHint}
        placeholder={bucketPlaceholder}
      />
    );
  };

  if (!connector) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Gradient top border */}
      <Box sx={{ height: 3, background: `linear-gradient(to right, ${brandColor}, ${alpha(brandColor, 0.4)})` }} />

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Connector Header */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(brandColor, 0.1),
                color: brandColor,
                flexShrink: 0,
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                {connector.display_name.charAt(0)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {isEditing ? `Edit ${connector.display_name} Connection` : `Connect to ${connector.display_name}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {connector.short_description}
              </Typography>
            </Box>
          </Stack>

          {/* Coming soon warning for uninstalled connectors */}
          {!isInstalled && (
            <Alert severity="info" sx={{ mt: 0 }}>
              <Typography variant="body2">
                <strong>{connector.display_name}</strong> connector is coming soon. The required source package is not yet installed in this environment.
              </Typography>
            </Alert>
          )}

          {/* Connection Name */}
          <TextField
            fullWidth
            size="small"
            label="Connection Name"
            value={connectorName}
            onChange={handleNameChange}
            error={!!nameError}
            helperText={nameError || "Give your connection a unique name"}
            required
            placeholder={`My ${connector.display_name}`}
            disabled={!isInstalled}
          />

          {/* Direction selector (only for "both" connectors) */}
          {connector.direction === 'both' && (
            <FormControl fullWidth size="small">
              <InputLabel>Use as</InputLabel>
              <Select
                value={connectorDirection}
                label="Use as"
                onChange={(e) => setConnectorDirection(e.target.value as 'source' | 'destination')}
              >
                <MenuItem value="source">Source (Read data from {connector.display_name})</MenuItem>
                <MenuItem value="destination">Destination (Write data to {connector.display_name})</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Project association checkbox (only when creating new connector and project is available) */}
          {!isEditing && projectId && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={associateWithProject}
                  onChange={(e) => setAssociateWithProject(e.target.checked)}
                  disabled={!isInstalled}
                  inputProps={{
                    'aria-label': 'Associate connector with current project',
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Associate with current project
                </Typography>
              }
            />
          )}

          {/* Config fields */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Configuration
            </Typography>
            <Stack spacing={2}>{connector.config_fields.map(renderConfigField)}</Stack>
          </Box>

          {/* Test result */}
          {testResult && (
            <Alert severity={testResult.success ? 'success' : 'error'}>
              {testResult.message}
            </Alert>
          )}

          {/* Stream discovery loading */}
          {discoveringStreams && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Discovering available {connector.category?.includes('Files') ? 'files' : 'tables'}...
              </Typography>
            </Stack>
          )}

          {/* Stream/table selection */}
          {availableStreams.length > 0 && !discoveringStreams && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Select {availableStreams[0]?.type === 'file' ? 'files' : 'objects'} to sync
                </Typography>
                <Link
                  component="button"
                  variant="caption"
                  onClick={handleSelectAllStreams}
                  sx={{ cursor: 'pointer' }}
                >
                  {selectedStreams.length === availableStreams.length ? 'Deselect all' : 'Select all'}
                </Link>
              </Stack>
              <Box
                sx={{
                  maxHeight: 200,
                  overflowY: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                }}
              >
                {availableStreams.map((stream) => (
                  <FormControlLabel
                    key={stream.name}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedStreams.includes(stream.name)}
                        onChange={() => handleStreamToggle(stream.name)}
                      />
                    }
                    label={
                      <Typography variant="body2" noWrap>
                        {stream.name}
                      </Typography>
                    }
                    sx={{ display: 'flex', width: '100%' }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedStreams.length} of {availableStreams.length} selected
              </Typography>
            </Box>
          )}

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleTestConnection}
          disabled={testLoading || !isInstalled}
          startIcon={testLoading ? <CircularProgress size={16} /> : <TestIcon />}
        >
          Test Connection
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || !isInstalled}>
          {isEditing ? 'Update Connection' : 'Save & Connect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectorSetupDialog;

